import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Fade,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Rating,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { API_URL } from "../../../Config/api"; // Adjust this path if necessary

// --- Hidden Input for Uploads ---
const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

// --- Premium Modal Styling ---
const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: 600, md: 700 }, // Wider for form layout
  bgcolor: "#ffffff",
  borderRadius: "16px",
  boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.12)",
  outline: "none",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh",
};

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fafafa",
    transition: "all 0.3s ease",
    "&:hover fieldset": { borderColor: "rgb(173, 219, 177)" },
    "&.Mui-focused fieldset": {
      borderColor: "rgb(173, 219, 177)",
      borderWidth: "2px",
    },
  },
};

// --- PROPS MEIN aggSpPtId ADD KIYA HAI ---
const FeedbackModal = ({ open, onClose, eventID, patientID, aggSpPtId }) => {
  const accessToken = localStorage.getItem("token");
  const addedby = localStorage.getItem("clg_id");

  // --- Feedback Logic States ---
  const [tabValue, setTabValue] = useState("patient");
  const [questions, setQuestions] = useState([]);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [textInputs, setTextInputs] = useState({});
  const [feedbackDataState, setFeedbackDataState] = useState([]);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [errors, setErrors] = useState({});
  const [options, setOptions] = useState([]);
  const [selectedDropdownValue, setSelectedDropdownValue] = useState("");
  const [dropdownError, setDropdownError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Snackbar States
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // --- 1. Fetch Professionals ---
  useEffect(() => {
    if (open && eventID) {
      const fetchProfessionalData = async () => {
        try {
          const response = await fetch(
            `${API_URL}/web/Prof_names_eve_wise/${eventID}`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          );
          const data = await response.json();
          setOptions(data);
        } catch (error) {
          console.error("Error fetching professional data:", error);
        }
      };
      fetchProfessionalData();
    }
  }, [eventID, accessToken, open]);

  // --- 2. Fetch Questions ---
  useEffect(() => {
    if (open) {
      const fetchQuestions = async () => {
        try {
          const response = await fetch(
            `${API_URL}/web/feedback_questions/?lang=eng`,
            {
              headers: { Authorization: `Bearer ${accessToken}` },
            },
          );
          const data = await response.json();
          setQuestions(data.data);
        } catch (error) {
          console.error("Error fetching feedback questions:", error);
        }
      };
      fetchQuestions();
    }
  }, [accessToken, open]);

  // --- 3. Fetch Existing Feedback Data on Dropdown Change ---
  const fetchFeedbackData = async () => {
    try {
      // API URL MEIN agg_sp_pt_id BHI ADD KIYA GAYA HAI (optional but safe)
      const response = await fetch(
        `${API_URL}/web/save_feedback_q/?eve_id=${eventID}&ptn_id=${patientID}&prof_id=${selectedDropdownValue}&agg_sp_pt_id=${aggSpPtId || ""}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );
      if (response.ok) {
        const data = await response.json();
        setFeedbackDataState(data);
        const selectedAnswersFromData = {};
        const textInputsFromData = {};
        let profId = "";

        data.forEach((item) => {
          selectedAnswersFromData[item.f_questions] = item.answer;
          textInputsFromData[item.f_questions] = "";
          if (item.prof_id) {
            profId = item.prof_id;
          }
        });

        setSelectedAnswers(selectedAnswersFromData);
        setTextInputs(textInputsFromData);
        if (profId) setSelectedDropdownValue(profId);
      } else {
        setFeedbackDataState([]);
        setSelectedAnswers({});
        setTextInputs({});
      }
    } catch (error) {
      console.error("Error fetching feedback data:", error);
      setFeedbackDataState([]);
      setSelectedAnswers({});
      setTextInputs({});
    }
  };

  useEffect(() => {
    if (selectedDropdownValue) {
      fetchFeedbackData();
    }
  }, [selectedDropdownValue]);

  // --- Handlers ---
  const handleTabChange = (event, newValue) => setTabValue(newValue);
  const handleChangeDropdown = (event) => {
    setSelectedDropdownValue(event.target.value);
    setDropdownError("");
  };
  const handleAnswerSelection = (questionId, selectedValue) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: selectedValue }));
  };
  const handleTextChange = (questionId, textValue) => {
    setTextInputs((prev) => ({ ...prev, [questionId]: textValue }));
  };
  const handleRatingChange = (questionId, ratingValue) => {
    setSelectedAnswers((prev) => ({ ...prev, [questionId]: ratingValue }));
  };
  const handleImageChange = (e) => setSelectedImage(e.target.files[0]);
  const handleVideoChange = (e) => setSelectedVideo(e.target.files[0]);

  // --- Validation ---
  const validateFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!selectedDropdownValue) {
      setDropdownError("Please select a professional");
      isValid = false;
    } else {
      setDropdownError("");
    }

    questions.forEach((question) => {
      if ([16, 17, 18].includes(question.F_questions)) {
        const answer =
          selectedAnswers[question.F_questions] ||
          textInputs[question.F_questions];
        if (!answer) {
          newErrors[question.F_questions] = "This field is required";
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // --- Form Submission ---
  const handlePatientSubmit = async () => {
    if (!validateFields()) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Please fill all mandatory (*) fields");
      setSnackbarOpen(true);
      return;
    }

    setIsSubmitting(true);
    const feedbackData = questions.map((question) => ({
      que: question.F_questions,
      ans:
        selectedAnswers[question.F_questions] ||
        textInputs[question.F_questions] ||
        "",
    }));

    try {
      const formData = new FormData();
      formData.append("eve_id", eventID);
    formData.append("ptn_id", patientID || aggSpPtId || "");
      formData.append("prof_id", selectedDropdownValue);

      // --- YAHAN PAYLOAD MEIN agg_sp_pt_id APPEND KIYA GAYA HAI ---
      if (aggSpPtId) {
        formData.append("agg_sp_pt_id", aggSpPtId);
      }

      if (selectedImage) formData.append("media", selectedImage);
      if (selectedVideo) formData.append("video", selectedVideo);
      formData.append("feedback", JSON.stringify(feedbackData));
      formData.append("added_by", addedby);

      const response = await fetch(`${API_URL}/web/save_feedback_q/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        localStorage.setItem("feedbackData", JSON.stringify(responseData));
        setFeedbackDataState(responseData);
        setSnackbarSeverity("success");
        setSnackbarMessage(
          responseData.message || "Feedback submitted successfully!",
        );
        setFormSubmitted(true);
        setSnackbarOpen(true);

        // Reset and close
        setTimeout(() => {
          setSelectedDropdownValue("");
          setSelectedAnswers({});
          setTextInputs({});
          onClose();
        }, 2000);
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to submit feedback");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Error submitting feedback");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  return (
    <>
      <Modal open={open} onClose={onClose} closeAfterTransition>
        <Fade in={open}>
          <Box sx={modalStyle}>
            {/* --- Premium Header --- */}
            <Box
              sx={{
                background: "rgb(173, 219, 177)",
                px: 3,
                py: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0, // Prevents header from squishing
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  color: "#ffffff",
                  letterSpacing: "0.5px",
                  textShadow: "0px 1px 2px rgba(0,0,0,0.1)",
                }}
              >
                Feedback Form
              </Typography>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  color: "#FFFFFF",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* --- Modal Body --- */}
            <Box
              sx={{
                p: 3,
                overflowY: "auto", // Allows scrolling inside the body only
                flexGrow: 1,
              }}
            >
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  mb: 3,
                  minHeight: "40px",
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "15px",
                    color: "#777",
                    "&.Mui-selected": { color: "rgb(130, 190, 135)" },
                  },
                  "& .MuiTabs-indicator": {
                    backgroundColor: "rgb(130, 190, 135)",
                  },
                }}
              >
                <Tab label="Patient Feedback" value="patient" />
              </Tabs>

              {tabValue === "patient" && (
                <Grid container spacing={3}>
                  {/* Professional Dropdown */}
                  <Grid item xs={12}>
                    <FormControl fullWidth size="medium" sx={inputStyle}>
                      <InputLabel id="dropdown-label">
                        Professional Name *
                      </InputLabel>
                      <Select
                        labelId="dropdown-label"
                        value={selectedDropdownValue}
                        label="Professional Name *"
                        onChange={handleChangeDropdown}
                      >
                        {options.map((option) => (
                          <MenuItem
                            key={option.srv_prof_id}
                            value={option.srv_prof_id}
                          >
                            {option.prof_name}
                          </MenuItem>
                        ))}
                      </Select>
                      {dropdownError && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 0.5, ml: 1 }}
                        >
                          {dropdownError}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {/* Questions Map */}
                  {questions.map((question, index) => (
                    <Grid key={index} item xs={12}>
                      <Box
                        sx={{
                          p: 2,
                          background: "#fdfdfd",
                          borderRadius: "12px",
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, color: "#333", mb: 1 }}
                        >
                          {question.question}
                          {[16, 17, 18].includes(question.F_questions) && (
                            <span
                              style={{ color: "#d32f2f", marginLeft: "4px" }}
                            >
                              *
                            </span>
                          )}
                        </Typography>

                        <FormControl component="fieldset" fullWidth>
                          {question.answer_options.length === 0 ? (
                            <>
                              <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Type your answer here..."
                                name={`feedback-${question.F_questions}`}
                                size="small"
                                sx={{ ...inputStyle, mt: 1 }}
                                value={
                                  textInputs[question.F_questions] ||
                                  selectedAnswers[question.F_questions] ||
                                  ""
                                }
                                onChange={(event) =>
                                  handleTextChange(
                                    question.F_questions,
                                    event.target.value,
                                  )
                                }
                                error={!!errors[question.F_questions]}
                                helperText={errors[question.F_questions]}
                              />
                            </>
                          ) : question.answer_options[0].star ? (
                            <Rating
                              name={`rating-${question.F_questions}`}
                              value={selectedAnswers[question.F_questions] || 0}
                              max={question.answer_options[0].star}
                              size="large"
                              onChange={(event, newValue) =>
                                handleRatingChange(
                                  question.F_questions,
                                  newValue,
                                )
                              }
                              sx={{ color: "#fbc02d" }}
                            />
                          ) : (
                            <RadioGroup
                              row
                              aria-label={`feedback-${question.F_questions}`}
                              name={`feedback-${question.F_questions}`}
                              value={
                                selectedAnswers[question.F_questions] || ""
                              }
                              onChange={(event) =>
                                handleAnswerSelection(
                                  question.F_questions,
                                  event.target.value,
                                )
                              }
                            >
                              {question.answer_options.map((answer, i) => (
                                <FormControlLabel
                                  key={i}
                                  value={answer}
                                  control={
                                    <Radio
                                      sx={{
                                        color: "rgb(173, 219, 177)",
                                        "&.Mui-checked": {
                                          color: "rgb(130, 190, 135)",
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      sx={{ fontSize: "14px", color: "#555" }}
                                    >
                                      {answer}
                                    </Typography>
                                  }
                                />
                              ))}
                            </RadioGroup>
                          )}
                        </FormControl>
                        {errors[question.F_questions] &&
                          question.answer_options.length !== 0 && (
                            <Typography
                              variant="caption"
                              color="error"
                              sx={{ display: "block", mt: 1 }}
                            >
                              {errors[question.F_questions]}
                            </Typography>
                          )}
                      </Box>
                    </Grid>
                  ))}

                  {/* Upload Buttons */}
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                          flex: 1,
                          borderRadius: "10px",
                          textTransform: "none",
                          color: "#7e57c2",
                          borderColor: "#7e57c2",
                          "&:hover": {
                            background: "#f3e5f5",
                            borderColor: "#5e35b1",
                          },
                        }}
                      >
                        {selectedImage ? selectedImage.name : "Upload Image"}
                        <VisuallyHiddenInput
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </Button>

                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{
                          flex: 1,
                          borderRadius: "10px",
                          textTransform: "none",
                          color: "#e53935",
                          borderColor: "#e53935",
                          "&:hover": {
                            background: "#ffebee",
                            borderColor: "#c62828",
                          },
                        }}
                      >
                        {selectedVideo ? selectedVideo.name : "Upload Video"}
                        <VisuallyHiddenInput
                          type="file"
                          accept="video/*"
                          onChange={handleVideoChange}
                        />
                      </Button>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  {/* Submit Button */}
                  <Grid item xs={12} sx={{ textAlign: "center", pb: 2 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handlePatientSubmit}
                      disabled={
                        formSubmitted ||
                        feedbackDataState.length > 0 ||
                        isSubmitting
                      }
                      sx={{
                        py: 1.5,
                        borderRadius: "10px",
                        fontWeight: 600,
                        fontSize: "15px",
                        background:
                          formSubmitted || feedbackDataState.length > 0
                            ? "#e0e0e0"
                            : "rgb(173, 219, 177)",
                        color:
                          formSubmitted || feedbackDataState.length > 0
                            ? "#757575"
                            : "#2c3e50",
                        boxShadow: "0px 4px 12px rgba(173, 219, 177, 0.4)",
                        textTransform: "none",
                        "&:hover": {
                          background: "rgb(143, 199, 147)",
                          boxShadow: "0px 6px 16px rgba(143, 199, 147, 0.5)",
                        },
                        "&.Mui-disabled": {
                          background: "#f5f5f5",
                          color: "#9e9e9e",
                        },
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : formSubmitted || feedbackDataState.length > 0 ? (
                        "Feedback Submitted"
                      ) : (
                        "Submit Feedback"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* --- Snackbar --- */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ borderRadius: "8px", fontWeight: 500 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default FeedbackModal;
